import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserById, updateUser, deleteUser } from '../../lib/supabase/users';
import {
    FiUser, FiMail, FiPhone, FiTruck, FiAlertCircle, FiSettings, FiArrowLeft,
    FiEdit2, FiSave, FiX, FiTrash2, FiClock, FiBriefcase, FiHome
} from 'react-icons/fi';

// Define components outside to prevent recreation on re-render
const EditableSection = ({ title, icon: Icon, section, editingSection, saving, onStartEdit, onCancel, onSave, children }) => {
    const isEditing = editingSection === section;
    return (
        <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Icon className="w-5 h-5 text-blue-600" />
                    {title}
                </h3>
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={onSave}
                                disabled={saving}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={onCancel}
                                disabled={saving}
                                className="px-4 py-2 bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-zinc-600 transition-colors"
                            >
                                <FiX className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onStartEdit}
                            className="px-4 py-2 bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors flex items-center gap-2"
                        >
                            <FiEdit2 className="w-4 h-4" />
                            Edit
                        </button>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
};

const InfoRow = ({ label, value, field, type = 'text', options, editData, setEditData, isEditing }) => {
    return (
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-zinc-700 last:border-0">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</span>
            {isEditing ? (
                type === 'select' ? (
                    <select
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-1.5 border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : type === 'date' ? (
                    <input
                        type="date"
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-1.5 border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                ) : (
                    <input
                        type={type}
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-1.5 border border-gray-300 dark:border-zinc-600 rounded-lg dark:bg-zinc-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-1/2"
                    />
                )
            ) : (
                <span className="text-sm text-gray-900 dark:text-white font-medium">
                    {value || <span className="text-gray-400">Not set</span>}
                </span>
            )}
        </div>
    );
};

const UserDetails = () => {
    const { userId } = useParams();
    const { companySlug } = useParams();
    const navigate = useNavigate();
    const { company } = useAuth();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingSection, setEditingSection] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchUser();
    }, [userId]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const data = await getUserById(userId);
            setUser(data);
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching user:', err);
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (section, data) => {
        setEditingSection(section);
        setEditData(data);
    };

    const cancelEditing = () => {
        setEditingSection(null);
        setEditData({});
    };

    const saveEdit = async () => {
        try {
            setSaving(true);
            const { user: updatedUser } = await updateUser(userId, editData);
            setUser(updatedUser);
            setEditingSection(null);
            setEditData({});
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Error updating user:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!window.confirm(`Are you sure you want to delete ${user.username}? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteUser(userId);
            navigate(`/${companySlug}/manage-users`);
        } catch (err) {
            setError(err.message);
            console.error('Error deleting user:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-zinc-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error && !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-zinc-900 px-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-md">
                    <div className="flex items-center gap-3 mb-2">
                        <FiAlertCircle className="w-6 h-6 text-red-600" />
                        <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">Error Loading User</h3>
                    </div>
                    <p className="text-red-700 dark:text-red-300">{error}</p>
                    <button
                        onClick={() => navigate(`/${companySlug}/manage-users`)}
                        className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                    >
                        Back to Users
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="">
            <div className="mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(`/${companySlug}/manage-users`)}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back to Users
                    </button>

                    <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6 shadow-sm">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                                {user.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt={user.username}
                                        className="w-20 h-20 rounded-full object-cover border-2 border-blue-600"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                        <span className="text-2xl font-bold text-white">
                                            {user.username.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                        {user.full_name || user.username}
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-400">@{user.username}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            user.role === 'super_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                                            user.role === 'admin' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                            user.role === 'designer' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                            user.role === 'estimator' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                            'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300'
                                        }`}>
                                            {user.role === 'super_admin' ? 'Super Admin' :
                                             user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            user.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                        }`}>
                                            {user.status === 'active' ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleDeleteUser}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                            >
                                <FiTrash2 className="w-4 h-4" />
                                Delete User
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                        <div className="flex items-center gap-2">
                            <FiAlertCircle className="w-5 h-5 text-red-600" />
                            <p className="text-red-700 dark:text-red-300">{error}</p>
                        </div>
                    </div>
                )}

                {/* Company Information (Read-only) */}
                {user.companies && (
                    <div className="mb-6 bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                            <FiHome className="w-5 h-5 text-blue-600" />
                            Company
                        </h3>
                        <div className="flex items-center gap-4">
                            {user.companies.logo_url ? (
                                <img
                                    src={user.companies.logo_url}
                                    alt={user.companies.name}
                                    className="w-16 h-16 rounded-xl border-2 border-gray-200 dark:border-zinc-700 object-cover"
                                />
                            ) : (
                                <div
                                    className="w-16 h-16 rounded-xl flex items-center justify-center"
                                    style={{
                                        background: `linear-gradient(135deg, ${user.companies.primary_color || '#3b82f6'} 0%, ${user.companies.secondary_color || '#8b5cf6'} 100%)`
                                    }}
                                >
                                    <FiHome className="w-8 h-8 text-white" />
                                </div>
                            )}
                            <div>
                                <h4 className="text-xl font-bold text-gray-900 dark:text-white">{user.companies.name}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">/{user.companies.slug}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Basic Information */}
                    <EditableSection
                        title="Basic Information"
                        icon={FiUser}
                        section="basic"
                        editingSection={editingSection}
                        saving={saving}
                        onStartEdit={() => startEditing('basic', user)}
                        onCancel={cancelEditing}
                        onSave={saveEdit}
                    >
                        <InfoRow label="Username" value={user.username} field="username"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'basic'} />
                        <InfoRow label="Full Name" value={user.full_name} field="full_name"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'basic'} />
                        <InfoRow label="Email" value={user.email} field="email" type="email"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'basic'} />
                        <InfoRow label="Phone" value={user.phone} field="phone" type="tel"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'basic'} />
                        <InfoRow label="Avatar URL" value={user.avatar_url} field="avatar_url" type="url"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'basic'} />
                    </EditableSection>

                    {/* Employment Information */}
                    <EditableSection
                        title="Employment Information"
                        icon={FiBriefcase}
                        section="employment"
                        editingSection={editingSection}
                        saving={saving}
                        onStartEdit={() => startEditing('employment', user)}
                        onCancel={cancelEditing}
                        onSave={saveEdit}
                    >
                        <InfoRow label="Position" value={user.position} field="position"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'employment'} />
                        <InfoRow label="Role" value={user.role} field="role" type="select"
                                 options={[
                                     { value: 'super_admin', label: 'Super Admin' },
                                     { value: 'admin', label: 'Admin' },
                                     { value: 'designer', label: 'Designer' },
                                     { value: 'estimator', label: 'Estimator' },
                                     { value: 'driver', label: 'Driver' }
                                 ]}
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'employment'} />
                        <InfoRow label="Status" value={user.status} field="status" type="select"
                                 options={[
                                     { value: 'active', label: 'Active' },
                                     { value: 'inactive', label: 'Inactive' }
                                 ]}
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'employment'} />
                    </EditableSection>

                    {/* Emergency Contact */}
                    <EditableSection
                        title="Emergency Contact"
                        icon={FiAlertCircle}
                        section="emergency"
                        editingSection={editingSection}
                        saving={saving}
                        onStartEdit={() => startEditing('emergency', user)}
                        onCancel={cancelEditing}
                        onSave={saveEdit}
                    >
                        <InfoRow label="Contact Name" value={user.emergency_contact_name} field="emergency_contact_name"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'emergency'} />
                        <InfoRow label="Contact Phone" value={user.emergency_contact_phone} field="emergency_contact_phone" type="tel"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'emergency'} />
                    </EditableSection>

                    {/* Vehicle Information */}
                    <EditableSection
                        title="Vehicle Information"
                        icon={FiTruck}
                        section="vehicle"
                        editingSection={editingSection}
                        saving={saving}
                        onStartEdit={() => startEditing('vehicle', user)}
                        onCancel={cancelEditing}
                        onSave={saveEdit}
                    >
                        <InfoRow label="License Number" value={user.license_number} field="license_number"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'vehicle'} />
                        <InfoRow label="License Expiry" value={formatDate(user.license_expiry)} field="license_expiry" type="date"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'vehicle'} />
                        <InfoRow label="Vehicle Type" value={user.vehicle_type} field="vehicle_type"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'vehicle'} />
                        <InfoRow label="Vehicle Registration" value={user.vehicle_registration} field="vehicle_registration"
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'vehicle'} />
                    </EditableSection>

                    {/* Preferences */}
                    <EditableSection
                        title="Preferences"
                        icon={FiSettings}
                        section="preferences"
                        editingSection={editingSection}
                        saving={saving}
                        onStartEdit={() => startEditing('preferences', user)}
                        onCancel={cancelEditing}
                        onSave={saveEdit}
                    >
                        <InfoRow label="Theme" value={user.theme_preference} field="theme_preference" type="select"
                                 options={[
                                     { value: 'light', label: 'Light' },
                                     { value: 'dark', label: 'Dark' },
                                     { value: 'system', label: 'System' }
                                 ]}
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'preferences'} />
                        <InfoRow label="Language" value={user.language} field="language" type="select"
                                 options={[
                                     { value: 'en', label: 'English' },
                                     { value: 'es', label: 'Spanish' },
                                     { value: 'fr', label: 'French' }
                                 ]}
                                 editData={editData} setEditData={setEditData}
                                 isEditing={editingSection === 'preferences'} />
                    </EditableSection>
                </div>

                {/* Metadata Section */}
                <div className="mt-6 bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                        <FiClock className="w-5 h-5 text-blue-600" />
                        Account Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Created At</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(user.created_at)}</p>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Last Updated</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(user.updated_at)}</p>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Last Login</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(user.last_login_at)}</p>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-lg">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Last Active</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(user.last_active_at)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
